import React, { useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import {
  AssignmentTurnedIn,
  Assignment,
  AccountCircle,
  AccountBalance,
  Apartment,
} from '@mui/icons-material';
import { useNavigate as useRouterNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader, PageContent } from '@viilutalous/common';
import { useTenant } from '../contexts/TenantContext';

/**
 * /me — personal landing page inside MeLayout (host scope, no tenant selector).
 */
export default function MyHome() {
  const { t } = useTranslation();
  const navigate = useRouterNavigate();
  const { currentTenant } = useTenant();

  const cards = useMemo(() => [
    {
      key: 'all-companies',
      title: t('me.cards.all_companies.title', 'Accounting firm'),
      description: t('me.cards.all_companies.description', 'Due work and balance coverage across companies.'),
      icon: <AccountBalance fontSize="large" />,
      onClick: () => navigate('/me/all-companies'),
    },
    {
      key: 'companies',
      title: t('me.cards.companies.title', 'My companies'),
      description: t('me.cards.companies.description', 'Every tenant you have access to — click to switch into one.'),
      icon: <Apartment fontSize="large" />,
      onClick: () => navigate('/me/companies'),
    },
    {
      key: 'approvals',
      title: t('me.cards.approvals.title', 'My approvals'),
      description: t('me.cards.approvals.description', 'Approve or reject items waiting for you across all your tenants.'),
      icon: <AssignmentTurnedIn fontSize="large" />,
      onClick: () => navigate('/me/approvals'),
    },
    {
      key: 'tasks',
      title: t('me.cards.tasks.title', 'My tasks'),
      description: t('me.cards.tasks.description', 'Open tasks assigned to you across all your tenants.'),
      icon: <Assignment fontSize="large" />,
      onClick: () => navigate('/me/tasks'),
    },
    {
      key: 'settings',
      title: t('me.cards.user_settings.title', 'User settings'),
      description: t('me.cards.user_settings.description', 'Profile, password, language, notifications.'),
      icon: <AccountCircle fontSize="large" />,
      onClick: () => {
        const slug = currentTenant?.slug;
        if (slug) navigate(`/app/${slug}/user-settings`);
        else navigate('/app');
      },
    },
  ], [t, navigate, currentTenant?.slug]);

  return (
    <>
      <PageHeader />
      <PageContent>
        <Stack spacing={3}>
          <Grid container spacing={2}>
            {cards.map((c) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={c.key}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardActionArea onClick={c.onClick} sx={{ height: '100%' }}>
                    <CardContent>
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        <Box sx={{ color: 'primary.main', mt: 0.5 }}>{c.icon}</Box>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {c.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {c.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Stack>
      </PageContent>
    </>
  );
}
